import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Divider } from '@/components/shared/element/divider';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Image } from '@/components/shared/media/image';
import { formatPrice, getPartPrefix } from '@/components/shared/utility/format';
import { MENU_LCV_SERVICE_PARTS } from '@/contents/lcv/link';
import { IPartDetail } from '@/types/shared/parts';

interface PartItemProps {
  cardUrl?: string;
  part: IPartDetail;
  index?: number;
  dataTrack?: string;
  dataTrackSection?: string;
  dataTest?: string;
}

const PRICE_STRINGS = {
  specialPriceLabel: 'พิเศษเริ่ม',
  regularPriceLabel: 'ปกติ',
  startingLabel: 'เริ่ม',
};

const PartImage = ({ src, alt }: { src: string; alt: string }) => (
  <Box
    backgroundColor="gray400"
    css={{
      transition:
        'background-color var(--transition-duration) var(--transition-easing)',
      '@hover': {
        '&:hover, [data-hover]:hover &': { backgroundColor: '$gray900' },
      },
    }}
  >
    <Image
      src={src}
      alt={alt || 'Part Image'}
      width={138}
      height={160}
      layout="responsive"
      aspectRatio="4by5"
      objectFit="contain"
      useSrcSetPattern
      sizes="(max-width: 479px) 280px, 320px"
    />
  </Box>
);

const PriceRow = ({
  label,
  value,
  dataTest,
}: {
  label?: string;
  value?: string;
  dataTest?: string;
}) => (
  <Box
    display="flex"
    columnGap="3"
    direction={{ '@initial': 'column', '@sm': 'row' }}
    justifyContent={{ '@sm': 'between' }}
  >
    {!!label && (
      <Text
        as="p"
        size="text-base-2"
        wrap
        dangerouslySetInnerHTML={{ __html: label }}
      />
    )}
    {!!value && (
      <Text
        as="p"
        size="text-base-2"
        font="bold"
        wrap
        dangerouslySetInnerHTML={{ __html: value }}
        data-test={dataTest}
      />
    )}
  </Box>
);

const PriceDetails = ({
  sub,
  index,
  dataTest,
}: {
  sub: any;
  index: number;
  dataTest: string;
}) => {
  if (!sub) return null;

  const { specialPrice, price, unit, isStartPrice } = sub;

  if (specialPrice) {
    return (
      <>
        <PriceRow
          label={PRICE_STRINGS.specialPriceLabel}
          value={`${formatPrice(specialPrice, false)} ${unit}`}
          dataTest={`${dataTest}_specialprice_${index}`}
        />
        {!!price && (
          <PriceRow
            label={PRICE_STRINGS.regularPriceLabel}
            value={`${formatPrice(price, false)} ${unit}`}
            dataTest={`${dataTest}_regularprice_${index}`}
          />
        )}
      </>
    );
  }

  return (
    <>
      {!!(
        sub.title ||
        sub.isIsuzuGenuinePart ||
        sub.isBvpPart ||
        sub.isTripetchGenuinePart
      ) ? (
        <PriceRow
          label={getPartPrefix(sub)}
          value={`${
            isStartPrice ? PRICE_STRINGS.startingLabel : ''
          } ${formatPrice(price, false)} ${unit}`}
          dataTest={`${dataTest}_price_${index}`}
        />
      ) : (
        <PriceRow
          label={!!isStartPrice ? PRICE_STRINGS.startingLabel : ''}
          value={`${formatPrice(price, false)} ${unit}`}
          dataTest={`${dataTest}_price_${index}`}
        />
      )}
    </>
  );
};

export const PartsItem = ({
  cardUrl,
  part,
  dataTrack = 'recommended-parts',
  dataTrackSection = 'recommended-parts',
  dataTest = 'partItem',
}: PartItemProps) => {
  if (!part) return null;

  const { title, itemImage, genuineParts = [], url } = part;

  return (
    <Box
      position="relative"
      display="flex"
      direction="column"
      rowGap={{ '@initial': '3', '@md': '4' }}
      height="full"
      test={{
        dataTest: `${dataTest}_item`,
      }}
      data-hover
    >
      {!!itemImage?.url && <PartImage src={itemImage.url} alt={title} />}
      <Box>
        {!!title && (
          <Box display="flex" justifyContent="between" columnGap="2">
            <Text
              as="h4"
              size="text-body-2"
              font="bold"
              data-test={`${dataTest}_title`}
              css={{
                paddingTop: '$space-0-5',
                '@xs': { paddingTop: '$space-0' },
              }}
            >
              {title}
            </Text>
            <ButtonIcon
              icon="arrow-forward"
              size="xs"
              isTransparent
              variant="button-icon-color-gray-dark"
              test={{ dataTest: `${dataTest}_button_icon` }}
            />
          </Box>
        )}
        {genuineParts.length > 0 && (
          <Box
            display="grid"
            rowGap={{ '@initial': '1', '@sm': '0' }}
            marginTop="1"
            test={{
              dataTest: `${dataTest}_genuineparts`,
            }}
          >
            {genuineParts.map((sub, i) => (
              <PriceDetails
                key={i}
                sub={sub}
                index={i}
                dataTest={`${dataTest}_genuinepart`}
              />
            ))}
          </Box>
        )}
      </Box>
      <Divider
        css={{
          marginTop: '$space-auto',
          transition:
            'background-color var(--transition-duration) var(--transition-easing)',
          '@hover': {
            '&:hover, [data-hover]:hover &': {
              backgroundColor: '$gray600',
            },
          },
        }}
      />
      <Link
        href={`${cardUrl}/${url}`}
        clickable
        tracking={{
          dataTrack: dataTrack,
          dataTrackSection: dataTrackSection,
          dataTrackText: title,
          dataTrackUrl: `${MENU_LCV_SERVICE_PARTS.url}/${url}`,
        }}
        test={{
          dataTest: `${dataTest}_link`,
        }}
      />
    </Box>
  );
};

PartsItem.displayName = 'PartsItem';
